import { ValuationCard } from '@dealstack/frontend/app/pages/Valuations/views/ValuationsHome/ValuationCard/ValuationCard';
import {
	StyledDealCardContainer,
	ValuationsHomeContainer,
} from '@dealstack/frontend/app/pages/Valuations/views/ValuationsHome/styled';
import { useValuationsQuery } from '@dealstack/frontend/app/pages/Valuations/queries/useValuationsQuery';
import { ValuationsEmpty } from '@dealstack/frontend/app/pages/Valuations/views/ValuationsHome/ValuationsEmpty/ValuationsEmpty';
import { useValuationBreadcrumbs } from '@dealstack/frontend/app/pages/Valuations/hooks/useValuationBreadcrumbs';
import { useValuationNavigation } from '@dealstack/frontend/app/pages/Valuations/hooks/useValuationNavigation';
import { useCallback, useMemo, useState } from 'react';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { ValuationsLoaderContainer } from '@dealstack/frontend/app/pages/Valuations/styled';
import { VALUATIONS_ROUTES } from '@dealstack/frontend/app/pages/Valuations/shared/valuationRoutes';
import { uuidv7 } from 'uuidv7';
import { useTranslationValuations } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Flex, Button } from '@mantine/core';
import { Tree } from '@dealstack/frontend/shared/components/Tree/Tree';
import { TreeItem } from '@dealstack/frontend/shared/components/Tree/utils';
import { useValuationClassifiers } from '@dealstack/frontend/app/pages/Valuations/hooks/useValuationClassifiers';
import { ValuationsWarnings } from '@dealstack/frontend/app/pages/Valuations/components/ValuationsWarnings/ValuationsWarnings';

import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag';
import { DashboardGrid } from '@dealstack/frontend/shared/layout/DashboardGrid/DashboardGrid.tsx';
import { VALUATION_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';

const VALUATIONS_PARENT_TAG = 'my-valuations';

export function ValuationsHome() {
	const { dealId, navigateTo } = useValuationNavigation();
	const {
		valuations,
		isLoading: isLoadingValuations,
		deleteValuation: deleteValuationMutation,
		duplicateValuation: duplicateValuationMutation,
	} = useValuationsQuery(dealId);
	const { valuationClassifiers } = useValuationClassifiers();
	const tValuations = useTranslationValuations();

	const [selectedIds, setSelectedIds] = useState<string[]>([]);

	const [expandedIds, setExpandedIds] = useState<string[]>(['my-valuations']);

	const { value: inPageFeatureFlag } = useDealstackFeatureFlag(
		FEATURE_FLAG.IN_PAGE_BREADCRUMBS_AND_QUICK_SWITCH_MENU
	);

	const treeItems: TreeItem[] = [
		{
			id: 'my-valuations',
			name: tValuations('homepage.title'),
			icon: 'home-v3',
			isFolder: true,
			selected: selectedIds.includes('my-valuations'),
			expanded: expandedIds.includes('my-valuations'),
			children: valuationClassifiers.map(({ id, name }) => ({
				id,
				name,
				icon: 'link-chain',
				isFolder: false,
				selected: selectedIds.includes(id),
				expanded: expandedIds.includes(id),
			})),
		},
	];

	const handleSelectionChange = useCallback((newSelectedIds: string[]) => {
		setSelectedIds((prevSelectedIds) => {
			const id = newSelectedIds[0];
			if (!id) {
				return prevSelectedIds;
			}

			return prevSelectedIds.includes(id)
				? prevSelectedIds.filter((prevId) => prevId !== id)
				: [...prevSelectedIds, id];
		});
	}, []);

	const handleExpandChange = useCallback((newExpandedIds: string[]) => {
		setExpandedIds((prevExpandedIds) => {
			const id = newExpandedIds[0];
			if (!id) {
				return prevExpandedIds;
			}

			return prevExpandedIds.includes(id)
				? prevExpandedIds.filter((prevId) => prevId !== id)
				: [...prevExpandedIds, id];
		});
	}, []);

	useValuationBreadcrumbs(
		{
			view: 'home',
			dealId,
		},
		inPageFeatureFlag
	);

	const filteredValuations = useMemo(() => {
		const tagsToFilter = selectedIds.filter((id) => id !== VALUATIONS_PARENT_TAG);

		if (tagsToFilter.length === 0) {
			return valuations;
		}

		const valuationTagSets = valuations.map(
			(valuation) => new Set(valuation.classifiers.map((classifier) => classifier.id))
		);

		return valuations.filter((_, index) =>
			tagsToFilter.every((tagId) => valuationTagSets[index].has(tagId))
		);
	}, [valuations, selectedIds]);

	if (!dealId) {
		return <div>{tValuations('homepage.errors.no_deal_id')}</div>;
	}

	if (isLoadingValuations) {
		return (
			<ValuationsLoaderContainer>
				<InlineLoader withoutBackground />
			</ValuationsLoaderContainer>
		);
	}

	return (
		<DashboardGrid
			pageTitle={tValuations('page_title')}
			hideBreadcrumbs={true}
			headerActions={
				<Flex gap="1rem" align="center">
					<Button
						data-testid={VALUATION_ELEMENT_ID.NEW_VALUATION_HEADER_BTN}
						onClick={() =>
							navigateTo('specific', {
								dealId: dealId ?? '',
								valuationId: uuidv7(),
							})
						}
						leftSection={<Icon variant="plus" size="lg" />}
					>
						{tValuations('new_valuation_button')}
					</Button>
					<ValuationsWarnings />
				</Flex>
			}
			sidebarContent={
				<Tree
					items={treeItems}
					onSelectionChange={handleSelectionChange}
					onExpandChange={handleExpandChange}
				/>
			}
			mainContent={
				filteredValuations.length <= 0 ? (
					<ValuationsEmpty
						createNewValuationButtonAction={() =>
							navigateTo('specific', {
								dealId,
								valuationId: uuidv7(),
							})
						}
					/>
				) : (
					<ValuationsHomeContainer>
						<StyledDealCardContainer>
							{filteredValuations.map((valuation, index) => (
								<ValuationCard
									key={index}
									to={VALUATIONS_ROUTES.summary({
										dealId,
										valuationId: valuation.id,
									})}
									valuationCardDetails={valuation}
									deleteValuation={deleteValuationMutation.mutate}
									duplicateValuation={duplicateValuationMutation.mutate}
								/>
							))}
						</StyledDealCardContainer>
					</ValuationsHomeContainer>
				)
			}
		/>
	);
}
